import { Grid, Tooltip, useMediaQuery, useTheme } from "@material-ui/core";
import { Block, Save } from "@material-ui/icons";
import { VALIDATE_MAXIMUM_EXPORT_CURRENT_QUANTITY } from "app/appConfig";
import CommonButton from "app/common/Component/Button/CommonButton";
import PopupForm from "app/common/Component/Popup/PopupForm";
import GlobitsAutocomplete from "app/common/form/GlobitsAutocomplete";
import GlobitsDateTimePicker from "app/common/form/GlobitsDateTimePicker";
import GlobitsNumberInput from "app/common/form/GlobitsNumberInput";
import GlobitsPagingAutocomplete from "app/common/form/GlobitsPagingAutocomplete";
import { getExportedMinSkuQuantity } from "app/common/inventoryUtils";
import IconDelete from "app/common/Icon/IconDelete";
import {
  formatMoney,
  getDepartmentsUser,
  isAdmin,
} from "app/common/LocalFunction";
import { pagingDepartment } from "app/services/DepartmentService";
import { pagingStoreConfig } from "app/services/StoreConfigService";
import { useStore } from "app/stores";
import { FieldArray, useFormikContext } from "formik";
import { observer } from "mobx-react";
import { memo } from "react";
import * as Yup from "yup";
// VALIDATE_MAXIMUM_EXPORT_CURRENT_QUANTITY

const RequestStoreExportProductsPopup = ({ patient }) => {
  const validationSchema = Yup.object().shape({
    department: Yup.object()
      .nullable()
      .required("Chưa chọn cơ sở!")
      .test("department-id", "Chưa chọn cơ sở!", (value) => !!value?.id),
    store: Yup.object()
      .nullable()
      .required("Chưa chọn kho yêu cầu!")
      .test(
        "store-id",
        "Chưa chọn kho yêu cầu!",
        (value) => !!(value?.id || value?.code)
      ),
    dateIssue: Yup.mixed()
      .nullable()
      .required("Chưa chọn ngày yêu cầu!")
      .test("date-valid", "Ngày yêu cầu không hợp lệ!", (value) => {
        if (!value) return false;
        const d = value instanceof Date ? value : new Date(value);
        return !Number.isNaN(d.getTime());
      }),

    items: Yup.array()
      .min(1, "Cần thêm ít nhất 1 đơn hàng!")
      .test(
        "at-least-one-export-qty",
        "Vui lòng nhập số lượng xuất lớn hơn 0 cho ít nhất một sản phẩm!",
        (items) =>
          Array.isArray(items) &&
          items.some((it) => Number(it?.exportedCurrentQuantity) > 0)
      )
      .of(
        Yup.object().shape({
          exportedCurrentQuantity: Yup.number()
            .transform((value, originalValue) => {
              if (
                originalValue === "" ||
                originalValue === null ||
                originalValue === undefined
              ) {
                return null;
              }
              return value;
            })
            .nullable()
            .typeError("Định dạng phải là số!")
            .test(
              "positive-qty",
              "Số lượng phải lớn hơn 0!",
              (value) => value == null || value === 0 || value > 0
            )
            .test(
              "require-used-unit",
              "Sản phẩm chưa có đơn vị tính, không thể xuất!",
              function (value) {
                if (!value || value <= 0) return true;
                const { usedUnit } = this.parent;
                return !!usedUnit;
              }
            )
            .test(
              "maximum-exported",
              "Số lượng xuất hiện tại phải nhỏ hơn tồn",
              function (value) {
                if (!value || value <= 0) return true;
                if (!VALIDATE_MAXIMUM_EXPORT_CURRENT_QUANTITY) return true;
                const { inventoryQuantity } = this.parent;
                // Chưa chọn kho → chưa biết tồn, không chặn ở đây (store là trường bắt buộc riêng).
                if (inventoryQuantity == null) return true;
                if (value > (inventoryQuantity || 0)) return false;
                return true;
              }
            )
            .test(
              "maximum-payment-exported",
              "Số lượng xuất hiện tại không được vượt qua số lượng đã thanh toán",
              function (value) {
                if (!value || value <= 0) return true;
                if (!VALIDATE_MAXIMUM_EXPORT_CURRENT_QUANTITY) return true;

                const { soldQuantity, exportedQuantity, item } = this.parent;
                if (!item?.amountActuallyPaid) return true;
                // Đã xuất RÒNG = đã xuất − đã hoàn (hoàn trả trả lại quyền xuất phần đã hoàn).
                const netExported =
                  (exportedQuantity || 0) - (item?.refundedQuantity || 0);
                let validQuantityExport =
                  Math.floor(
                    (item?.totalBillPatientPaid / item?.amountActuallyPaid) *
                      soldQuantity
                  ) - netExported;

                if (value > validQuantityExport) return false;
                return true;
              }
            ),
        })
      )
      .nullable(),
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));

  const {
    initRequestStoreExportProducts,
    handleExportProductsFromHis,
    openRequestStoreExportProductsPopup,
    handleCloseRequestStoreExportProductsPopup,
  } = useStore().patientManagementStore.registerProductStore;

  return (
    <PopupForm
      open={openRequestStoreExportProductsPopup}
      size="md"
      handleClose={handleCloseRequestStoreExportProductsPopup}
      title={`Xuất kho`}
      formik={{
        initialValues: {
          ...initRequestStoreExportProducts,
          patientId: patient?.id,
        },
        onSubmit: handleExportProductsFromHis,
        enableReinitialize: true,
        validationSchema: validationSchema,
      }}
      action={({ isSubmitting }) => {
        return (
          <div className="flex-center gap-1">
            <CommonButton
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {!isMobile && <Save className="mr-2 " />}
              {`Xác nhận`}
            </CommonButton>

            <CommonButton
              className="btn-gray"
              onClick={handleCloseRequestStoreExportProductsPopup}
            >
              <Block className="mr-2" /> Đóng
            </CommonButton>
          </div>
        );
      }}
    >
      {({ setFieldValue, values }) => {
        return (
          <>
            <ValueForm />
          </>
        );
      }}
    </PopupForm>
  );
};

const ValueForm = observer(() => {
  const { setFieldValue, values } = useFormikContext();
  const departmentsUser = getDepartmentsUser();
  let isAdminPermission = isAdmin();

  const { handleInitStoreRequest } =
    useStore().patientManagementStore.registerProductStore;
  return (
    <>
      <FieldArray name="items">
        {({ push, remove }) => (
          <>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={4} md={4}>
                <GlobitsPagingAutocomplete
                  api={
                    isAdminPermission
                      ? pagingDepartment
                      : async () => ({
                          data: {
                            content: departmentsUser || [],
                            totalElements: departmentsUser?.length || 0,
                            totalPages: 1,
                          },
                        })
                  }
                  name={"department"}
                  label="Cơ sở"
                  required
                  onChange={async (_, value) => {
                    setFieldValue("department", value);
                    setFieldValue("store", value?.storeConfig || null);
                    handleInitStoreRequest(value, value?.storeConfig);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <GlobitsAutocomplete
                  options={
                    values?.department?.departmentStoreConfigs?.map(
                      (el) => el?.storeConfig
                    ) || []
                  }
                  api={pagingStoreConfig}
                  name={"store"}
                  label="Kho yêu cầu"
                  getOptionLabel={(option) => {
                    if (option === null) {
                      return "---";
                    }
                    if (option?.name) {
                      return option?.name;
                    }
                  }}
                  onChange={async (_, value) => {
                    setFieldValue("store", value);
                    handleInitStoreRequest(values?.department, value);
                  }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={4} md={4}>
                <GlobitsDateTimePicker
                  name={"dateIssue"}
                  label="Ngày yêu cầu"
                  maxDateMessage="Ngày đăng ký phải trước ngày hiện tại"
                  maxDate={new Date()}
                  isDateTimePicker
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <div className="table-root mt-2">
                  <div style={{ minWidth: "420px" }}>
                    <table className="table-container">
                      <thead>
                        <tr className="row-table-header">
                          <th width="5%">STT</th>
                          <th width="17%">Tên sản phẩm</th>
                          <th width="9%">Đơn vị tính</th>
                          <th width="9%">SL đăng ký</th>
                          <th width="9%">SL đã xuất</th>
                          <th width="9%">SL đã hoàn</th>
                          <th width="10%">Còn có thể xuất</th>
                          <th width="10%">SL xuất hiện tại</th>
                          <th width="13%">Đơn vị xuất kho</th>
                          <th width="12%">Tồn (ĐV nhỏ nhất)</th>
                          {/* <th width="10%">Tồn khả dụng</th> */}
                          <th width="8%">Thao tác</th>
                        </tr>
                      </thead>
                      <tbody>
                        {values?.items?.length > 0 &&
                          values?.items?.map((item, index) => {
                            return (
                              <tr key={index}>
                                <td align="center">{index + 1}</td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                    }}
                                  >
                                    <span className="service-name">
                                      {item?.drug?.name}
                                    </span>
                                    <i>
                                      <span>
                                        <Tooltip title="Đã thu">
                                          <b style={{ color: "green" }}>
                                            {formatMoney(
                                              +item?.item
                                                ?.totalBillPatientPaid || 0
                                            )}
                                          </b>
                                        </Tooltip>
                                      </span>
                                      /
                                      <span>
                                        <Tooltip title="Cần thu">
                                          <b style={{ color: "red" }}>
                                            {formatMoney(
                                              item?.item?.amountActuallyPaid ||
                                                0
                                            )}
                                          </b>
                                        </Tooltip>
                                      </span>
                                    </i>
                                  </div>
                                </td>
                                <td>{item?.usedUnit?.name}</td>
                                <td>
                                  <GlobitsNumberInput
                                    name={`items[${index}].soldQuantity`}
                                    required
                                    disabled
                                  />
                                </td>
                                <td>
                                  <GlobitsNumberInput
                                    name={`items[${index}].exportedQuantity`}
                                    required
                                    disabled
                                  />
                                </td>
                                <td align="center">
                                  <b style={{ color: "#e67e22" }}>
                                    {item?.item?.refundedQuantity || 0}
                                  </b>
                                </td>
                                <td align="center">
                                  {(() => {
                                    const refunded =
                                      item?.item?.refundedQuantity || 0;
                                    const netExported =
                                      (item?.exportedQuantity || 0) - refunded;
                                    const paid = item?.item?.amountActuallyPaid;
                                    const allowed = paid
                                      ? Math.floor(
                                          (item?.item?.totalBillPatientPaid /
                                            paid) *
                                            (item?.soldQuantity || 0)
                                        )
                                      : item?.soldQuantity || 0;
                                    const remain = Math.max(
                                      0,
                                      allowed - netExported
                                    );
                                    return (
                                      <b style={{ color: "green" }}>{remain}</b>
                                    );
                                  })()}
                                </td>
                                <td>
                                  <GlobitsNumberInput
                                    name={`items[${index}].exportedCurrentQuantity`}
                                    required
                                  />
                                </td>
                                <td align="center">
                                  {item?.exportUnitResolved ? (
                                    <span>
                                      <b>
                                        {getExportedMinSkuQuantity(
                                          item?.exportedCurrentQuantity,
                                          item?.coefficient
                                        )}
                                      </b>{" "}
                                      {item?.exportUnitName || ""}
                                    </span>
                                  ) : (
                                    <span
                                      style={{ color: "red" }}
                                      title="Chưa map được đơn vị/hệ số quy đổi qua mã"
                                    >
                                      Chưa xác định
                                    </span>
                                  )}
                                </td>
                                <td align="center">
                                  {item?.inventoryMinSkuQuantity == null ? (
                                    <span
                                      style={{ color: "#888" }}
                                      title="Chọn kho để xem tồn"
                                    >
                                      —
                                    </span>
                                  ) : (
                                    <>
                                      <span>
                                        <b>{item?.inventoryMinSkuQuantity}</b>{" "}
                                        {item?.exportUnitName || ""}
                                      </span>
                                      {item?.coefficient > 1 &&
                                        item?.usedUnit?.name && (
                                          <i
                                            style={{
                                              display: "block",
                                              color: "#888",
                                              fontSize: 12,
                                            }}
                                          >
                                            (≈ {item?.inventoryQuantity ?? 0}{" "}
                                            {item?.usedUnit?.name})
                                          </i>
                                        )}
                                    </>
                                  )}
                                </td>
                                {/* <td>
                                  <GlobitsNumberInput
                                    name={`items[${index}].availableAmount`}
                                    required
                                    disabled
                                  />
                                </td> */}

                                <td align="center">
                                  <span
                                    tooltip={"Xóa"}
                                    className="pointer tooltip"
                                    onClick={() => remove(index)}
                                  >
                                    <IconDelete />
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </Grid>
            </Grid>
          </>
        )}
      </FieldArray>
    </>
  );
});

export default memo(observer(RequestStoreExportProductsPopup));
